import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SchedulePage } from './pages/SchedulePage';
import { RegisterPage } from './pages/RegisterPage';
import { CheckInOutPage } from './pages/CheckInOutPage';
import { MakeupCheckInOutPage } from './pages/MakeupCheckInOutPage';
import { ManagementCheckInOutPage } from './pages/ManagementCheckInOutPage';
import { LoginPage } from './pages/LoginPage';
import { SalaryInfoPage } from './pages/SalaryInfoPage';
import { FileSharePage } from './pages/FileSharePage';
import { AdminBulletinPage } from './pages/AdminBulletinPage';
import { DisciplineRewardStatsPage } from './pages/DisciplineRewardStatsPage';
import { BulletinBoardPage } from './pages/BulletinBoardPage';
import { UrgentBulletinModal } from './components/UrgentBulletinModal';
import { Navigation } from './components/Navigation';
import { UserSidebar } from './components/UserSidebar';
import { AuthGuard } from './components/AuthGuard';
import { LoadingProvider } from './contexts/LoadingContext';
import { ErrorProvider } from './contexts/ErrorContext';
import { InventoryEntryPage } from './pages/InventoryEntryPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { pagePermissions } from './utils/permissions';
import './App.css';

// Layout component for regular users (with sidebar)
const checkIsMobile = () => window.innerWidth < 768 || (window.innerHeight <= 500 && window.innerWidth <= 950);

const UserLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMobile, setIsMobile] = useState(checkIsMobile());

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(checkIsMobile());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="user-layout-shell" style={{ display: 'flex', minHeight: '100vh' }}>
      <UserSidebar />
      <div style={{ 
        marginLeft: isMobile ? '0' : '250px', 
        flex: 1, 
        padding: isMobile ? '60px 10px 20px 10px' : '20px',
        width: isMobile ? '100%' : 'calc(100% - 250px)',
        boxSizing: 'border-box',
        transition: 'margin-left 0.3s ease, padding 0.3s ease'
      }}>
        {children}
      </div>
    </div>
  );
};

// Về trang đầu tiên người dùng được phép
const AdminRedirect: React.FC = () => {
  const { user } = useAuth();
  return <Navigate to={user?.homePath || '/checkinout'} replace />;
};

function App() {
  const [currentPage, setCurrentPage] = useState('schedule');

  return (
    <AuthProvider>
      <LoadingProvider>
        <ErrorProvider>
          <Router>
            <div className="app">
              <Routes>
                {/* Login is the only public route */}
                <Route path="/login" element={
                  <AuthGuard requireAuth={false}>
                    <LoginPage />
                  </AuthGuard>
                } />
                <Route path="/nhap-hang" element={<AuthGuard><UserLayout><InventoryEntryPage /></UserLayout></AuthGuard>} />
                <Route path="/register" element={
                  <AuthGuard>
                    <UserLayout>
                      <RegisterPage />
                    </UserLayout>
                  </AuthGuard>
                } />
                <Route path="/register/:startDate/:endDate" element={
                  <AuthGuard>
                    <UserLayout>
                      <RegisterPage />
                    </UserLayout>
                  </AuthGuard>
                } />
                <Route path="/checkinout" element={
                  <AuthGuard>
                    <UserLayout>
                      <CheckInOutPage />
                    </UserLayout>
                  </AuthGuard>
                } />
                <Route path="/makeup-checkinout" element={
                  <AuthGuard>
                    <UserLayout>
                      <MakeupCheckInOutPage />
                    </UserLayout>
                  </AuthGuard>
                } />
                <Route path="/salary" element={
                  <AuthGuard>
                    <UserLayout>
                      <SalaryInfoPage />
                    </UserLayout>
                  </AuthGuard>
                } />
                <Route path="/bulletins" element={
                  <AuthGuard>
                    <UserLayout>
                      <BulletinBoardPage />
                    </UserLayout>
                  </AuthGuard>
                } />
                
                {/* Protected routes - authentication required */}
                <Route path="/" element={
                  <AuthGuard>
                    <AdminRedirect />
                  </AuthGuard>
                } />
                <Route path="/schedule" element={
                  <AuthGuard permissions={pagePermissions('/schedule')}>
                    <Navigation currentPage={currentPage} onPageChange={setCurrentPage} />
                    <main className="main-content">
                      <SchedulePage />
                    </main>
                  </AuthGuard>
                } />
                <Route path="/schedule/:startDate/:endDate" element={
                  <AuthGuard permissions={pagePermissions('/schedule')}>
                    <Navigation currentPage={currentPage} onPageChange={setCurrentPage} />
                    <main className="main-content">
                      <SchedulePage />
                    </main>
                  </AuthGuard>
                } />
                <Route path="/management-checkinout" element={
                  <AuthGuard permissions={pagePermissions('/management-checkinout')}>
                    <Navigation currentPage={currentPage} onPageChange={setCurrentPage} />
                    <main className="main-content">
                      <ManagementCheckInOutPage />
                    </main>
                  </AuthGuard>
                } />
                <Route path="/vault-share-2026" element={
                  <AuthGuard permissions={pagePermissions('/vault-share-2026')}>
                    <Navigation currentPage={currentPage} onPageChange={setCurrentPage} />
                    <main className="main-content">
                      <FileSharePage />
                    </main>
                  </AuthGuard>
                } />
                <Route path="/admin-bulletins" element={
                  <AuthGuard permissions={pagePermissions('/admin-bulletins')}>
                    <Navigation currentPage={currentPage} onPageChange={setCurrentPage} />
                    <main className="main-content">
                      <AdminBulletinPage />
                    </main>
                  </AuthGuard>
                } />
                <Route path="/admin-discipline-rewards" element={
                  <AuthGuard permissions={pagePermissions('/admin-discipline-rewards')}>
                    <Navigation currentPage={currentPage} onPageChange={setCurrentPage} />
                    <main className="main-content">
                      <DisciplineRewardStatsPage />
                    </main>
                  </AuthGuard>
                } />
              </Routes>
              <UrgentBulletinModal />
            </div>
          </Router>
        </ErrorProvider>
      </LoadingProvider>
    </AuthProvider>
  );
}

export default App;
